import React from 'react';
import { HashRouter as Router, Route } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { I18nextProvider } from 'react-i18next';
import { QueryParamProvider } from 'use-query-params';
import { Provider as AlertProvider, positions } from 'react-alert';
import i18next from 'i18next';
import moment from 'moment';
import 'moment/locale/it';
import {
  AuthProvider,
  AdminRoute,
  PrivateRoute,
  PublicRoute,
  RoutesWrapper,
} from './commons/Auth';
import AlertTemplate from './commons/AlertTemplate';
import { LoadingProvider } from './commons/Loading';
import Login from './pages/Login';
import Forgot from './pages/Forgot';
import Reset from './pages/Reset';
import Signup from './pages/Signup';
import Verification from './pages/Verification';
import Dashboard from './pages/Dashboard';
import Profiles from './pages/Profiles';
import ProfileDetail from './pages/ProfileDetail';
import Info from './pages/Info';
import PageNotFound from './pages/PageNotFound';
import translations from './translations';
import './App.scss';
import DashboardDetail from './pages/DashboardDetail';
import DashboardDetailCompensi from './pages/DashboardDetailCompensi';
import MyProfile from './pages/MyProfile';
import MyFinancialReports from './pages/MyFinancialReports';
import MyAffiliations from './pages/MyAffiliations';
import MyInvoices from './pages/MyInvoices';
import MyRoyalties from './pages/MyRoyalties';
import MyInsurances from './pages/MyInsurances';
import DashboardListMasters from './pages/DashboardListMasters';
import Logs from './pages/Logs';
import InvoicesByMonth from './pages/InvoicesByMonth';
// import Insurances from './pages/Insurances';
import Network from './pages/Network';
import ReportProfiles from './pages/ReportProfiles';
import MyStudioInvoices from './pages/MyStudioInvoices';
import Condos from './pages/Condos';
import EonCondos from './pages/EonCondos';
import SailInvoices from './pages/SailInvocies';
import DashboardDetailCompensiStudio from './pages/DashboardDetailCompensiStudio';
import MyEmittedIAF from './pages/MyEmittedIAF';
import MyReceivedIAF from './pages/MyReceivedIAF';
import Contracts from './pages/Contracts';
import ContractsResume from './pages/ContractsResume';
import ContractsRecurrencyResume from './pages/ContractsRecurrencyResume';
import MyReceivedExtraInvoice from './pages/MyReceivedExtraInvoice';
import CustomersExtra from './pages/CustomerExtra';
import CustomerExtraMasters from './pages/CustomerExtraMasters';
import Clients from './pages/Clients';
import Albo from './pages/Albo';
import MyNetworkFinancialReports from './pages/MyNetworkFinancialReports';
import CreditCalculation from './pages/CreditCalculation';
import PartnerCondos from './pages/PartnerCondos';
import OverdueInvoices from './pages/OverdueInvoices';
import MyCollaborators from './pages/MyCollaborators';
import MyCollaboratorsDetail from './pages/MyCollaboratorsDetail';
import Activities from './pages/Activities';
import ActivitiesDetail from './pages/ActivitiesDetail';
import Agreements from './pages/Agreements';
import AgreementsDetail from './pages/AgreementsDetail';
import AgreementsEditor from './pages/AgreementsEditor';
import AgreementsModeling from './pages/AgreementsModeling';
import Notifications from './pages/Notifications';
import IafsByMonth from './pages/IafsByMonth';
import RecurrentContracts from './pages/RecurrentContracts';
import AdminInfos from './pages/AdminInfos';
import SailInvoicesPanoramica from './pages/SailInvoicesPanoramica';
import DashboardDetailAssicurazioniCompensi from './pages/DashboardDetailAssicurazioniCompensi';
import ChooseService from './pages/ChooseService';
import InsurancesByMonthPanoramica from './pages/InsurancesByMonthPanoramica';

import PanoramicaIafsByMonth from './pages/PanoramicaIafsByMonth';
import UserProfiles from './pages/UserProfiles';
import UserProfileDetail from './pages/UserProfileDetail';
import EonCondosDetail from './pages/EonCondosDetail';
import MyEmittedIAFPanoramica from './pages/MyEmittedIAFPanoramica';
import ContractsSuperAdmin from './pages/ContractsSuperAdmin';
import ClaimsAdmin from './pages/ClaimsAdmin';
import InsurancesAdmin from './pages/InsurancesAdmin';
import MySignals from './pages/MySignals';
import SignalsResume from './pages/SignalsResume';
import MyGiveAndTake from './pages/MyGiveAndTake';
import MyGiveAndTakeInfo from './pages/MyGiveAndTake/InfoPresentational/InfoPresentational';
import MyGiveAndTakeIafs from './pages/MyGiveAndTake/InfoPresentationalRecived/InfoPresentational';
import SignalsAdmin from './pages/SignalsAdmin';
import MySignalsSent from './pages/MySignalsSent';
import IafsByMonthNetwork from './pages/IafsByMonthNetwork';
import AgreementsAdmin from './pages/AgreementsAdmin';
import AgreementsDetailAdmin from './pages/AgreementsDetailAdmin';
import ExternalAgreements from './pages/ExternalAgreements';
import ExternalAgreementsAdmin from './pages/ExternalAgreementsAdmin';
import ScheduledReporting from './pages/ScheduledReporting';
import Contacts from './pages/Contacts';
import Interactions from './pages/Interactions';
import QrCodes from './pages/QrCodes';
import AlboMarketing from './pages/AlboMarketing';
import Offices from './pages/Offices';
import MyRecurringFees from './pages/MyRecurringFee';

i18next.init({
  resources: translations,
  lng: 'it',
  keySeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

moment.locale('it');

export const FINANCIAL_REPORTS_ALLOWED_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'ADMIN_AGENT',
  'AGENCY',
]);

export const AFFILIATIONS_ALLOWED_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'ADMIN_AGENT',
  'SUPPLIER',
  'AGENCY',
]);

export const INVOICES_ALLOWED_TYPES = Object.freeze(['SUPPLIER', 'COMMERCIAL']);

export const ROYALTIES_ALLOWED_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'ADMIN_AGENT',
  'AGENCY',
]);

export const AGENT_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'AGENCY',
]);

export const SIGNALS_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'AGENCY',
  'SUPPLIER',
  'COMMERCIAL',
  'EON_ENERGY',
  'ASSOCIATION',
]);

export const PROFILE_DOCUMENTS_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'SUPPLIER',
  'ADMIN_COLLAB',
]);

export const ADMIN_TYPES = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
]);

export const DELETE_INVOICES_TYPE = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'SUPPLIER',
]);

export const ENERGY_USERS = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'EON_ENERGY',
  'AGENCY',
]);

export const ENERGY_USERS_ADMIN = Object.freeze([
  'BUILDING_ADMIN_1',
  'BUILDING_ADMIN_2',
  'BUILDING_ADMIN_3',
  'BUILDING_ADMIN_4',
  'BUILDING_ADMIN_5',
  'AGENCY',
  'COMMERCIAL',
  'ASSOCIATION',
]);

export const CUSTOMERS_ALLOWED_TYPES = Object.freeze([
  'SUPPLIER',
  'EON_ENERGY',
]);

export const FEE_ELETTRIC = Object.freeze([
  'DUFERCO_RES_LIBERTA_PER_TE_LUCE',
  'DUFERCO_RES_PIANETA_BLU_LUCE',
  'DUF_ENTRY_LUCE',
  'DUF_VIVACE_LUCE',
  'DUF_ALLEGRO_LUCE',
  'DUF_ANDANTE_LUCE',
  'DUF_ADAGIO_LUCE',
  'DOMESTICO_LUCE',
  'PIVA_LUCE',
  'BERGAMO_GAS_E_LUCE_BASE',
  'BERGAMO_GAS_E_LUCE_CAT12018',
  'BERGAMO_GAS_E_LUCE_CAT15014',
  'BERGAMO_GAS_E_LUCE_GAS_BASE',
  'BERGAMO_GAS_E_LUCE_CATGN1214',
  'BERGAMO_GAS_E_LUCE_CATGN1512',
  'ADAPTA_CASA_SICURA',
  'ADAPTA_CASA_DINAMICA',
  'ADAPTA_CASA_UNICA',
  'ADAPTA_CASA_CONV',
  'ADAPTA_BUSINESS_ESSENZIALE',
  'ADAPTA_BUSINESS_CONV',
  'ADAPTA_BUSINESS_SICURA',
  'ADAPTA_BUSINESS_DINAMICA',
  'ADAPTA_BUSINESS_UNICA',
  'ADAPTA_BUSINESS_PIU',
  'EON_LUCE_MERCATO_EASY',
  'EON_LUCE_MERCATO_PLUS_EASY',
  'EON_LUCE_MERCATO_PLUS_PREMIUM',
  'DUFERCO_LUCE_NETTUNO',
  'DUFERCO_LUCE_SATURNO',
  'DUFERCO_LUCE_VENERE',
  'DUFERCO_LUCE_MARTE',
  'EE_PLATINO_SUPER_BOLLETTA',
  'EE_DIAMANTE',
  'EE_ORO',
  'EE_ARGENTO',
  'ADPT100',
  'ADPT90',
  'ADPT80',
  'ADPT70',
  'ADPT60',
  'ADPT50',
  'ADPT40',
  'ADPT30',
  'ADPT20',
  'ADPT10',
  'DOMESTICO_EXTRA_EE_0',
  'DOMESTICO_EXTRA_EE_1',
  'DOMESTICO_EXTRA_EE_2',
  'DOMESTICO_EXTRA_EE_3',
  'DOMESTICO_EXTRA_EE_4',
  'BUSINESS_EXTRA_EE_1',
  'BUSINESS_EXTRA_EE_2',
  'BUSINESS_EXTRA_EE_3',
  'BUSINESS_EXTRA_EE_4',
  'ALTO_VALORE_7_15_EE',
  'ALTO_VALORE_8_20_EE',
  'ALTO_VALORE_9_30_EE',
  'ALTO_VALORE_10_35_EE',
  'CONDOMINIO_EXTRA_EE_1',
  'CONDOMINIO_EXTRA_EE_2',
  'CONDOMINIO_EXTRA_EE_3',
  'CONDOMINIO_EXTRA_EE_4',
  'CONDOMINIO_EXTRA_EE_5',
  'CONDOMINIO_EXTRA_EE_6',
  'CONDOMINIO_EXTRA_EE_7',
  'CONDOMINIO_EXTRA_EE_8',
  'CONDOMINIO_EXTRA_EE_9',
  'CONDOMINIO_EXTRA_EE_10',
]);

export const FEE_GAS = Object.freeze([
  'DUFERCO_RES_PIANETA_BLU_GAS',
  'DUFERCO_RES_LIBERTA_PER_TE_GAS',
  'DUF_ENTRY_GAS',
  'DUF_VIVACE_GAS',
  'DUF_ALLEGRO_GAS',
  'DUF_ANDANTE_GAS',
  'DUF_ADAGIO_GAS',
  'DOMESTICO_GAS',
  'PIVA_GAS',
  'EON_GAS_CERTO_NEW',
  'EON_GAS_CERTO_PREMIUM_NEW',
  'EON_DINAMICA_SEMPLICE_PIU',
  'DUFERCO_GAS_GIOVE',
  'DUFERCO_GAS_NETTUNO',
  'DUFERCO_GAS_MARTE',
  'INDEX_BUSINESS_SCONTO_6',
  'INDEX_BUSINESS_SCONTO_2',
  'DUFERCO_GAS_VENERE',
  'GAS_PLATINO_SUPER_BOLLETTA',
  'GAS_DIAMANTE',
  'GAS_ORO',
  'GAS_ARGENTO',
  'CASASICURA',
  'CASAUNICA',
  'CASADINAMICA',
  'CASA_CONV80',
  'CASA_CONV70',
  'TARIFFA_CONSUMI_ALTI',
  'GEOFLEX_BUSINESS_CONV90',
  'GEOFLEX_BUSINESS_CONV80',
  'GEOFLEX_BUSINESS_ESSENZIALE',
  'GEOFLEX_BUSINESS_CERTA',
  'GEOFLEX_BUSINESS_SICURA',
  'GEOFLEX_BUSINESS_DINAMICA',
  'GEOFLEX_COND_115',
  'GEOFLEX_COND_105',
  'GEOFLEX_COND_95',
  'GEOFLEX_COND_85',
  'GEOFLEX_COND_75',
  'GEOFLEX_COND_65',
  'GEOFLEX_COND_55',
  'GEOFLEX_COND_45',
  'GEOFLEX_COND_35',
  'GEOFLEX_COND_25',
  'GEOFLEX_COND_15',
  'DOMESTICO_EXTRA_GAS_0',
  'DOMESTICO_EXTRA_GAS_1',
  'DOMESTICO_EXTRA_GAS_2',
  'DOMESTICO_EXTRA_GAS_3',
  'DOMESTICO_EXTRA_GAS_4',
  'BUSINESS_EXTRA_GAS_1',
  'BUSINESS_EXTRA_GAS_2',
  'BUSINESS_EXTRA_GAS_3',
  'BUSINESS_EXTRA_GAS_4',
  'ALTO_VALORE_7_15_GAS',
  'ALTO_VALORE_8_20_GAS',
  'ALTO_VALORE_9_30_GAS',
  'ALTO_VALORE_10_35_GAS',
  'CONDOMINIO_EXTRA_GAS_1',
  'CONDOMINIO_EXTRA_GAS_2',
  'CONDOMINIO_EXTRA_GAS_3',
  'CONDOMINIO_EXTRA_GAS_4',
  'CONDOMINIO_EXTRA_GAS_5',
  'CONDOMINIO_EXTRA_GAS_6',
  'CONDOMINIO_EXTRA_GAS_7',
  'CONDOMINIO_EXTRA_GAS_8',
  'CONDOMINIO_EXTRA_GAS_9',
  'CONDOMINIO_EXTRA_GAS_10',
  'CONDOMINIO_EXTRA_GAS_11',
]);

export const POLIS_FEE_FIXED = Object.freeze([
  'DOMESTICO_EXTRA_GAS_1',
  'DOMESTICO_EXTRA_GAS_0',
  'DOMESTICO_EXTRA_GAS_1',
  'DOMESTICO_EXTRA_GAS_2',
  'DOMESTICO_EXTRA_GAS_3',
  'DOMESTICO_EXTRA_GAS_4',
  'BUSINESS_EXTRA_GAS_1',
  'BUSINESS_EXTRA_GAS_2',
  'BUSINESS_EXTRA_GAS_3',
  'BUSINESS_EXTRA_GAS_4',
]);

export const POLIS_FEE = Object.freeze([
  'DOMESTICO_EXTRA_EE_0',
  'DOMESTICO_EXTRA_EE_1',
  'DOMESTICO_EXTRA_EE_2',
  'DOMESTICO_EXTRA_EE_3',
  'DOMESTICO_EXTRA_EE_4',
  'BUSINESS_EXTRA_EE_1',
  'BUSINESS_EXTRA_EE_2',
  'BUSINESS_EXTRA_EE_3',
  'BUSINESS_EXTRA_EE_4',
  'ALTO_VALORE_7_15_EE',
  'ALTO_VALORE_8_20_EE',
  'ALTO_VALORE_9_30_EE',
  'ALTO_VALORE_10_35_EE',
  'CONDOMINIO_EXTRA_EE_1',
  'CONDOMINIO_EXTRA_EE_2',
  'CONDOMINIO_EXTRA_EE_3',
  'CONDOMINIO_EXTRA_EE_4',
  'CONDOMINIO_EXTRA_EE_5',
  'CONDOMINIO_EXTRA_EE_6',
  'CONDOMINIO_EXTRA_EE_7',
  'CONDOMINIO_EXTRA_EE_8',
  'CONDOMINIO_EXTRA_EE_9',
  'CONDOMINIO_EXTRA_EE_10',
  'DOMESTICO_EXTRA_GAS_0',
  'DOMESTICO_EXTRA_GAS_1',
  'DOMESTICO_EXTRA_GAS_2',
  'DOMESTICO_EXTRA_GAS_3',
  'DOMESTICO_EXTRA_GAS_4',
  'BUSINESS_EXTRA_GAS_1',
  'BUSINESS_EXTRA_GAS_2',
  'BUSINESS_EXTRA_GAS_3',
  'BUSINESS_EXTRA_GAS_4',
  'ALTO_VALORE_7_15_GAS',
  'ALTO_VALORE_8_20_GAS',
  'ALTO_VALORE_9_30_GAS',
  'ALTO_VALORE_10_35_GAS',
  'CONDOMINIO_EXTRA_GAS_1',
  'CONDOMINIO_EXTRA_GAS_2',
  'CONDOMINIO_EXTRA_GAS_3',
  'CONDOMINIO_EXTRA_GAS_4',
  'CONDOMINIO_EXTRA_GAS_5',
  'CONDOMINIO_EXTRA_GAS_6',
  'CONDOMINIO_EXTRA_GAS_7',
  'CONDOMINIO_EXTRA_GAS_8',
  'CONDOMINIO_EXTRA_GAS_9',
  'CONDOMINIO_EXTRA_GAS_10',
  'CONDOMINIO_EXTRA_GAS_11',
]);

export const CUSTOMERS_CONDOS_ALLOWED_TYPES = Object.freeze(['SUPPLIER']);

export const INSURANCES_ALLOWED_TYPES = Object.freeze(['SUPPLIER_INS']);

export const AGENCY_ALLOWED_TYPES = Object.freeze(['AGENCY']);

export const USER_ADMIN_TYPES = Object.freeze({
  SUPER_ADMIN: 'SUPER_ADMIN',
  SUPERVISOR: 'SUPERVISOR',
  SUPERVISOR_USER: 'SUPERVISOR_USER',
});

export const CERTIFIER_TYPE = Object.freeze({ CERTIFIER: 'CERTIFIER' });

function App() {
  return (
    <div className="App">
      <I18nextProvider i18n={i18next}>
        <AlertProvider
          template={AlertTemplate}
          position={positions.TOP_RIGHT}
          timeout={5000}
        >
          <CookiesProvider>
            <AuthProvider>
              <LoadingProvider>
                <Router>
                  <QueryParamProvider ReactRouterRoute={Route}>
                    <RoutesWrapper
                      adminRedirect="/dashboard"
                      publicRedirect="/"
                      privateRedirect="/my-profile"
                    >
                      <PublicRoute exact path="/" component={Login} />
                      <PublicRoute path="/signup" component={Signup} />
                      <PublicRoute path="/forgot" component={Forgot} />
                      <PublicRoute path="/reset" component={Reset} />
                      <PublicRoute
                        path="/verification"
                        component={Verification}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard"
                        component={Dashboard}
                      />
                      <AdminRoute exact path="/condos" component={Condos} />
                      <AdminRoute
                        path="/signals-admin"
                        component={SignalsAdmin}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard/compensi"
                        component={DashboardDetailCompensi}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard/compensi-studio"
                        component={DashboardDetailCompensiStudio}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard/masters"
                        component={DashboardListMasters}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard/assicurazioni"
                        component={DashboardDetailAssicurazioniCompensi}
                      />
                      <AdminRoute
                        exact
                        path="/dashboard/:clientType"
                        component={DashboardDetail}
                      />

                      <AdminRoute exact path="/profiles" component={Profiles} />
                      <AdminRoute
                        path="/profiles/:id"
                        component={ProfileDetail}
                      />
                      <AdminRoute exact path="/historic" component={Profiles} />
                      <AdminRoute
                        path="/historic/:id"
                        component={ProfileDetail}
                      />

                      <PrivateRoute
                        exact
                        path="/my-give-and-take/iafs"
                        component={MyGiveAndTakeIafs}
                        allow={[
                          ...ADMIN_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'AGENCY',
                          'EON_ENERGY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />
                      <PrivateRoute
                        exact
                        path="/my-give-and-take/info"
                        component={MyGiveAndTakeInfo}
                        allow={[
                          ...ADMIN_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'AGENCY',
                          'EON_ENERGY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />
                      <PrivateRoute
                        exact
                        path="/my-give-and-take"
                        component={MyGiveAndTake}
                        allow={[
                          ...ADMIN_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'AGENCY',
                          'EON_ENERGY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />

                      <PrivateRoute
                        path="/my-signals-received"
                        component={MySignals}
                        allow={[
                          ...ADMIN_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'AGENCY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />

                      <PrivateRoute
                        path="/my-signals-sent"
                        component={MySignalsSent}
                        allow={[
                          ...ADMIN_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'AGENCY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />

                      <PrivateRoute
                        exact
                        path="/user-profiles"
                        component={UserProfiles}
                        allow={['EON_ENERGY']}
                      />
                      <PrivateRoute
                        path="/user-profiles/:id"
                        component={UserProfileDetail}
                        allow={['EON_ENERGY']}
                      />

                      <PrivateRoute
                        path="/my-collaborators"
                        component={MyCollaborators}
                        allow={ADMIN_TYPES}
                      />
                      <PrivateRoute
                        path="/choose-service"
                        component={ChooseService}
                      />
                      {/* <AdminRoute path="/insurances" component={Insurances} /> */}
                      <AdminRoute
                        path="/contracts-admin"
                        component={ContractsSuperAdmin}
                      />
                      <AdminRoute
                        path="/insurances-admin"
                        component={InsurancesAdmin}
                      />
                      <AdminRoute
                        path="/claims-admin"
                        component={ClaimsAdmin}
                      />
                      <AdminRoute
                        path="/by-month"
                        component={InvoicesByMonth}
                      />
                      <AdminRoute
                        path="/panoramica-by-month"
                        component={InsurancesByMonthPanoramica}
                      />
                      <AdminRoute
                        path="/iafs-by-month"
                        component={IafsByMonth}
                      />
                      <AdminRoute
                        path="/panoramica-iafs-by-month"
                        component={PanoramicaIafsByMonth}
                      />
                      <AdminRoute
                        path="/iafs-by-month-network"
                        component={IafsByMonthNetwork}
                      />
                      {/*<AdminRoute*/}
                      {/*  exact*/}
                      {/*  path="/report-profiles"*/}
                      {/*  component={ReportProfiles}*/}
                      {/*/>*/}
                      <AdminRoute path="/logs" component={Logs} />
                      <AdminRoute
                        exact
                        path="/customer-extra-masters"
                        component={CustomerExtraMasters}
                      />
                      <AdminRoute
                        exact
                        path="/offices-extra-masters"
                        component={Offices}
                      />
                      <PrivateRoute path="/my-profile" component={MyProfile} />
                      <PrivateRoute path="/my-condos" component={Condos} />
                      <PrivateRoute
                        path="/partner-condos"
                        component={PartnerCondos}
                        allow={CUSTOMERS_ALLOWED_TYPES}
                      />
                      <PrivateRoute path="/contracts" component={Contracts} />
                      <PrivateRoute
                        path="/my-recurring-fees"
                        component={MyRecurringFees}
                        allow={[
                          ...ENERGY_USERS,
                          'ADMIN_COLLAB',
                          'UTILIZER',
                          'COMMERCIAL',
                          'ASSOCIATION',
                        ]}
                      />
                      <PrivateRoute
                        path="/recurrent-contracts"
                        component={RecurrentContracts}
                      />
                      <PrivateRoute
                        path="/my-eon-condos"
                        component={EonCondos}
                      />
                      <PrivateRoute
                        path="/contracts-resume"
                        component={ContractsResume}
                      />
                      <PrivateRoute
                        path="/contracts-recurrency-resume"
                        component={ContractsRecurrencyResume}
                      />
                      <PrivateRoute
                        path="/signals-resume"
                        component={SignalsResume}
                      />
                      <PrivateRoute
                        path="/my-financial-reports"
                        component={MyFinancialReports}
                        allow={[
                          ...FINANCIAL_REPORTS_ALLOWED_TYPES,
                          'COMMERCIAL',
                          'SUPPLIER',
                          'EON_ENERGY',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />
                      <PrivateRoute
                        path="/my-network-financial-reports"
                        component={MyNetworkFinancialReports}
                        allowAttr="commercial"
                      />
                      <PrivateRoute
                        path="/my-affiliations"
                        component={MyAffiliations}
                        allow={[
                          ...AFFILIATIONS_ALLOWED_TYPES,
                          'UTILIZER',
                          'ADMIN_COLLAB',
                        ]}
                      />
                      <PrivateRoute
                        path="/network"
                        component={Network}
                        allowAttr="commercial"
                      />
                      <PrivateRoute
                        path="/my-invoices"
                        component={MyInvoices}
                        allow={INVOICES_ALLOWED_TYPES}
                      />
                      <PrivateRoute
                        path="/my-by-month"
                        component={InvoicesByMonth}
                        allow={INVOICES_ALLOWED_TYPES}
                      />
                      <PrivateRoute
                        path="/my-royalties"
                        component={MyRoyalties}
                        allow={[
                          ...ROYALTIES_ALLOWED_TYPES,
                          'ADMIN_COLLAB',
                          'UTILIZER',
                          'COMMERCIAL',
                          'ASSOCIATION',
                        ]}
                      />
                      <PrivateRoute
                        path="/my-studio-invoices"
                        component={MyStudioInvoices}
                        allow={[...ROYALTIES_ALLOWED_TYPES, 'ADMIN_COLLAB']}
                      />
                      <AdminRoute
                        path="/my-emitted-iaf"
                        component={MyEmittedIAF}
                      />
                      <AdminRoute
                        path="/panoramica-my-emitted-iaf"
                        component={MyEmittedIAFPanoramica}
                      />
                      <PrivateRoute
                        path="/my-received-extra-invoice"
                        component={MyReceivedExtraInvoice}
                        allow={['EON_ENERGY', 'SUPPLIER', 'COMMERCIAL']}
                      />
                      <PrivateRoute
                        path="/my-received-iaf"
                        component={MyReceivedIAF}
                        allow={[
                          ...ROYALTIES_ALLOWED_TYPES,
                          'COMMERCIAL',
                          'ASSOCIATION',
                          'ADMIN_COLLAB',
                        ]}
                      />
                      <AdminRoute
                        path="/sail-invoices"
                        component={SailInvoices}
                      />
                      <AdminRoute
                        path="/panoramica-sail-invoices"
                        component={SailInvoicesPanoramica}
                      />
                      <AdminRoute
                        path="/scheduled-reporting"
                        component={ScheduledReporting}
                      />
                      <AdminRoute
                        path="/overdue-invoices"
                        component={OverdueInvoices}
                      />
                      <AdminRoute path="/archive" component={AdminInfos} />

                      <PrivateRoute
                        path="/offices"
                        component={Offices}
                        allow={['ASSOCIATION', 'SUPPLIER', 'EON_ENERGY']}
                      />

                      <PrivateRoute
                        path="/customer-extra"
                        component={CustomersExtra}
                        allow={[
                          'BUILDING_ADMIN_1',
                          'BUILDING_ADMIN_2',
                          'BUILDING_ADMIN_3',
                          'BUILDING_ADMIN_4',
                          'BUILDING_ADMIN_5',
                          'AGENCY',
                          'COMMERCIAL',
                          'ADMIN_COLLAB',
                          'ASSOCIATION',
                        ]}
                      />

                      <PrivateRoute
                        path="/my-customers"
                        component={Clients}
                        allow={CUSTOMERS_ALLOWED_TYPES}
                      />

                      <PrivateRoute
                        path="/albo"
                        component={Albo}
                        allow={[
                          ...ENERGY_USERS,
                          ...CUSTOMERS_ALLOWED_TYPES,
                          'ADMIN_COLLAB',
                          'COMMERCIAL',
                          'ASSOCIATION',
                        ]}
                      />

                      <PrivateRoute
                        path="/albo-marketing"
                        component={AlboMarketing}
                        allow={['MARKETING']}
                      />

                      <PrivateRoute
                        path="/contacts"
                        component={Contacts}
                        allow={['MARKETING']}
                      />

                      <PrivateRoute
                        path="/interactions"
                        component={Interactions}
                        allow={['MARKETING']}
                      />

                      <PrivateRoute
                        path="/qr-codes"
                        component={QrCodes}
                        allow={['MARKETING']}
                      />

                      {/* Area attivita */}
                      <PrivateRoute
                        path="/activities"
                        component={Activities}
                        allow={[...ADMIN_TYPES, 'ADMIN_COLLAB']}
                      />

                      <PrivateRoute
                        path="/activities-detail/:id"
                        component={ActivitiesDetail}
                        allow={[...ADMIN_TYPES, , 'ADMIN_COLLAB']}
                      />

                      <AdminRoute
                        path="/activities-admin"
                        component={Activities}
                      />

                      {/* Area contratti */}
                      <PrivateRoute
                        path="/agreements"
                        component={Agreements}
                        allow={[...ADMIN_TYPES, 'SUPPLIER', 'ADMIN_COLLAB']}
                      />

                      <PrivateRoute
                        path="/external-agreements"
                        component={ExternalAgreements}
                        allow={[...ADMIN_TYPES, 'SUPPLIER', 'ADMIN_COLLAB']}
                      />

                      <AdminRoute
                        path="/agreements-admin"
                        component={AgreementsAdmin}
                      />

                      <AdminRoute
                        path="/external-agreements-admin"
                        component={ExternalAgreementsAdmin}
                      />

                      <AdminRoute
                        path="/agreements-details-admin/:id"
                        component={AgreementsDetailAdmin}
                      />

                      <PrivateRoute
                        path="/agreements-details/:id"
                        component={AgreementsDetail}
                        allow={[...ADMIN_TYPES, 'SUPPLIER', 'ADMIN_COLLAB']}
                      />

                      <PrivateRoute
                        path="/agreements-modeling/:id"
                        component={AgreementsModeling}
                        allow={[...ADMIN_TYPES, 'SUPPLIER', 'ADMIN_COLLAB']}
                      />

                      <PrivateRoute
                        path="/agreements-editor/:id"
                        component={AgreementsEditor}
                        allow={[...ADMIN_TYPES, 'SUPPLIER', 'ADMIN_COLLAB']}
                      />

                      <PrivateRoute
                        path="/credit-calculation"
                        component={CreditCalculation}
                        allow={ADMIN_TYPES}
                      />

                      <PrivateRoute
                        path="/notifications"
                        component={Notifications}
                      />

                      <PrivateRoute path="/info" component={Info} />
                      <Route component={PageNotFound} />
                    </RoutesWrapper>
                  </QueryParamProvider>
                </Router>
              </LoadingProvider>
            </AuthProvider>
          </CookiesProvider>
        </AlertProvider>
      </I18nextProvider>
    </div>
  );
}

export default App;
